/**
 * Dependents — the census a catalog delete door carries back when it refuses:
 * what still stands in the way of destroying the document, in counts.
 * `deleteEvent`, `deleteTicketType` and `deleteLocation` each read their own
 * rows inside the transaction that would delete, so the refusal is decided on
 * live truth rather than on a stored "has sales" latch that drifts away from
 * the records it claims to describe.
 *
 * A wire shape, not a stored one: nothing persists it, so it carries no
 * `entity()` declaration and no Firestore adaptor. Every row defaults to 0
 * and each door fills only the rows it can actually query — an event has no
 * child locations, a location has no tickets — so a door constructs the rows
 * it censused and lets the schema supply the rest.
 *
 * Rows justified — each is something that still resolves through the target
 * and is left dangling the moment it is destroyed, a stored reference or an
 * outstanding claim:
 *   orders         — an order nests under its event and stores no eventId, so
 *                    the event door counts it by containment; its lines name
 *                    a ticket type by id and copy no name, which is what
 *                    leaves every invoice re-render and export reading
 *                    through the live catalog. The ticket-type door leaves
 *                    this row at zero — a line's typeId sits inside an array
 *                    of maps, which no query reaches, and `tickets` plus
 *                    `consumed` are what stand in for it there.
 *   tickets        — a ticket names its type by id and its event by path,
 *                    and the scanner resolves both at scan time; the event
 *                    door counts it by containment, the ticket-type door by
 *                    the typeId reference.
 *   consumed       — the one row that counts a claim rather than a document:
 *                    capacity drawn against a ticket type, read off that
 *                    type's own capacity ledger, standing whether or not the
 *                    tickets it was drawn for are minted yet. It and
 *                    `tickets` are complementary — a fully refunded type
 *                    reads tickets above zero and consumed at zero, a
 *                    mid-fulfilment one reads the reverse.
 *   openCheckouts  — a checkout still able to reach settle would mint an
 *                    order against a document the delete has already
 *                    removed; the event door counts it by containment,
 *                    narrowed to the statuses that still have an outgoing
 *                    edge.
 *   spendingCampaigns — a campaign whose money is committed at Meta keeps
 *                    spending against a landing page the delete would remove,
 *                    and the ad network is not this deployment's to reach into
 *                    afterwards. The event door counts it by containment,
 *                    narrowed to the statuses outside `campaign.entity.ts`'s
 *                    own `NO_SPEND_STATUSES`. That set is exactly what the
 *                    stop can act on, so the count carries one remedy: a
 *                    campaign with nothing built for it is swept with the
 *                    event rather than refusing a delete the operator would
 *                    then have no door to clear.
 *   events         — an event's locationId is required and never nullable;
 *                    the PDF, the .ics and the wallet pass resolve the venue
 *                    at fulfilment time, after the order was placed. The
 *                    location door counts it by that field.
 *   ticketTypes    — a type's locationId room override points at a venue the
 *                    same way, and is the reference a venue's own event list
 *                    cannot see: the location door reaches it across every
 *                    event at once, never through one event's catalog.
 *   childLocations — a room composes its parent venue's address at render
 *                    rather than duplicating it, so a removed parent leaves
 *                    the room rendering from its own fields alone; the
 *                    location door counts it by the room's within pointer.
 * Excluded:
 *   the ids or names of the blocking documents — a census that carries them
 *                    is unbounded on exactly the events that most need it,
 *                    and the refusal's job is to name the kind of thing in
 *                    the way and the remedy, not to enumerate it.
 *   a total, or a `blocked` boolean — the sum is the rows added up and the
 *                    fact of refusal is the outcome discriminant itself.
 *   a per-status checkout or campaign breakdown — the remedy is the same
 *                    whichever non-terminal status a checkout sits in: wait
 *                    for it to retire; and a campaign is counted only in the
 *                    statuses the stop acts on, so splitting that row names
 *                    one remedy several times.
 *   the campaigns the cascade sweeps — a census carries what stands in the
 *                    way of a delete, and a swept campaign stands in the way
 *                    of nothing; what went with the event is the door's own
 *                    record of what it destroyed.
 */
import { z } from "zod";
import { NO_SPEND_STATUSES } from "./campaign.entity.ts";
import { TERMINAL_STATUSES } from "./checkout.entity.ts";

export const DependentsSchema = z
	.object({
		orders: z
			.number()
			.int()
			.min(0)
			.default(0)
			.describe(
				"orders under the event being deleted, at census time — the event door reports it by subcollection containment; the ticket-type door leaves it at zero, because a line's typeId is not queryable",
			),
		tickets: z
			.number()
			.int()
			.min(0)
			.default(0)
			.describe(
				"tickets counted against the document being deleted, valid, redeemed and cancelled alike — the event door reports every ticket under the event by subcollection containment, the ticket-type door those whose typeId names the type",
			),
		consumed: z
			.number()
			.int()
			.min(0)
			.default(0)
			.describe(
				"seats already drawn against the ticket type being deleted, read off that type's own capacity ledger — counted whether or not the tickets they were drawn for are minted yet; the ticket-type door reports it",
			),
		openCheckouts: z
			.number()
			.int()
			.min(0)
			.default(0)
			.describe(
				`checkouts under the event being deleted whose status is none of ${TERMINAL_STATUSES.join(", ")} — the event door reports it by subcollection containment`,
			),
		spendingCampaigns: z
			.number()
			.int()
			.min(0)
			.default(0)
			.describe(
				`campaigns under the event being deleted whose status is none of ${NO_SPEND_STATUSES.join(", ")} — money committed at the ad network, still spending against a landing page the delete would remove; the event door reports it by subcollection containment`,
			),
		events: z
			.number()
			.int()
			.min(0)
			.default(0)
			.describe(
				"events whose locationId names the location being deleted — the location door reports it",
			),
		ticketTypes: z
			.number()
			.int()
			.min(0)
			.default(0)
			.describe(
				"ticket types across every event whose locationId room override names the location being deleted — the location door reports it",
			),
		childLocations: z
			.number()
			.int()
			.min(0)
			.default(0)
			.describe(
				"locations whose within names the location being deleted as their parent venue — the location door reports it",
			),
	})
	.strict();
export type Dependents = z.infer<typeof DependentsSchema>;
// A door constructs only the rows it censused; the schema fills the rest.
export type DependentsInput = z.input<typeof DependentsSchema>;
